import React, { useEffect, type ReactNode } from 'react';
import cn from 'classnames';

import App from '../../containers/App';
import ModalApp from '../../containers/ModalApp';
import Sidebar from '../../containers/Sidebar';
import Error from '../../containers/Error';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { MODAL_ELEMENT_ID } from '../../constants';
import { SubscriptionSuccessModal } from '../Modals/SubscriptionSuccessModal';
import { Banners } from '../Banners';
import type { MicroAppInstance } from '../AppLoader';
import type { GlobalsTools } from '../../shared/types';
import type { CurrentModalAppState } from '../../reducers/currentModalApp';
import type { DeprecatedRouter } from '../../deprecated-router';

type MicroAppAction = Parameters<
  NonNullable<MicroAppInstance['dispatchAction']>
>[0];

type GetInstance = () => MicroAppInstance | null | undefined;

// `name` is part of the real `tools` object built in src/index.js, but isn't
// modeled on the shared GlobalsTools type since most consumers don't need
// it. `router` overrides GlobalsTools' narrowed Omit<..., 'subscribe' |
// 'destroy'> back to the full DeprecatedRouter — Main is the one consumer
// that genuinely needs the app-wide bootstrap/teardown lifecycle.
interface MainGlobals extends GlobalsTools {
  name?: string;
  router: DeprecatedRouter;
}

export interface MainProps {
  children?: ReactNode;
  sidebarShown: boolean;
  modalOpen: boolean;
  currentModalApp?: Partial<CurrentModalAppState>;
  onModalAppLoaded: (instance: { destroy?: () => void } | null) => void;
  onModalCloseClick: () => MicroAppAction;
  changeLocation: (url: string) => void;
}

const proxyAction =
  (instances: GetInstance[]) =>
  (action: MicroAppAction): void => {
    instances.forEach((getInstance) => {
      const instance = getInstance();
      if (!instance || typeof instance.dispatchAction !== 'function') return;
      instance.dispatchAction(action);
    });
  };

let app: MicroAppInstance | null | undefined;
let sidebar: MicroAppInstance | null | undefined;

const wrapInModal = (
  reactComponent: ReactNode,
  onCloseClick: () => MicroAppAction,
  isModalOpen: boolean | undefined,
  addHeader = false
) => (
  <div
    className={cn('as-Modalcontainer', {
      'is-open': isModalOpen,
    })}
    onClick={() => {
      proxyAction([() => sidebar, () => app])(onCloseClick());
    }}
  >
    <div
      className={cn('as-Modal', {
        'is-open': isModalOpen,
      })}
      id={MODAL_ELEMENT_ID}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {addHeader && (
        <div className="as-Modalheader">
          <button
            type="button"
            className="as-Modalclose"
            onClick={onCloseClick}
          >
            <shore-icon name="delete" />
          </button>
        </div>
      )}
      {reactComponent}
    </div>
  </div>
);

export function Main({
  children,
  sidebarShown,
  modalOpen,
  currentModalApp = {},
  onModalAppLoaded,
  onModalCloseClick,
  changeLocation,
}: MainProps) {
  const deps = useGlobals() as MainGlobals;

  useEffect(() => {
    const { router } = deps;

    router.onChangeLocation(changeLocation, true);

    router.subscribe();

    return router.destroy;
  }, []);

  return (
    <div className="as-Appwrapper">
      <div
        className={cn('as-Body', { 'is-showingSidebar': sidebarShown })}
        id="asBody"
      >
        <App
          key={deps.name}
          {...deps}
          onProxyAction={proxyAction([() => sidebar])}
          onInstance={(instance) => {
            app = instance;
          }}
        />
        <Sidebar
          {...deps}
          onProxyAction={proxyAction([() => app])}
          onInstance={(instance) => {
            sidebar = instance;
          }}
        />
        {wrapInModal(null, onModalCloseClick, modalOpen)}
      </div>
      {wrapInModal(
        <ModalApp
          {...{
            ...deps,
            ...currentModalApp,
            onInstance(instance: MicroAppInstance | null | undefined) {
              if (instance) {
                onModalAppLoaded(instance);
              }
            },
          }}
        />,
        onModalCloseClick,
        currentModalApp.visible,
        true
      )}
      <Error />
      {children}
      <SubscriptionSuccessModal />
      <Banners />
    </div>
  );
}

export default Main;
